/* Grupo comExt da DPS. Só aparece em operação com o exterior: tomador ou local da prestação
   fora do Brasil, ou tributação "Exportação de serviço" (obrigatório nesses casos, E0330). */
import React, { useEffect } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import { useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputForm } from '../../../../../../components/inputs/forms/CurrencyInputForm';
import { InputForm } from '../../../../../../components/inputs/forms/InputForm';
import { extractErrorMessage } from '../../../../../../utils/ErrorUtils';
import {
  MEC_PRESTADOR_OPCOES,
  MEC_TOMADOR_OPCOES,
  MODO_PRESTACAO_OPCOES,
  MOEDA_OPCOES,
  MOV_TEMP_BENS_OPCOES,
  VINCULO_OPCOES,
} from '../../shared/constants';
import { PaisBusca } from '../../shared/PaisBusca';
import { C } from '../../shared/ui';
import { FormSection } from './FormSection';

export function CampoLista({
  name,
  label,
  opcoes,
  placeholder,
  isRequired,
  isDisabled,
  ajuda,
}: {
  name: string;
  label: string;
  opcoes: { value: string; label: string }[];
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  ajuda?: string;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const error = _.get(errors, name);
  return (
    <FormControl isRequired={isRequired} isInvalid={!!error} mb={5}>
      <FormLabel fontWeight="bold" mb={1} htmlFor={name}>
        {label}
      </FormLabel>
      <Select id={name} placeholder={placeholder} isDisabled={isDisabled} {...register(name)}>
        {opcoes.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
      {ajuda && !error && (
        <Text fontSize="12px" mt="6px" color={C.subtle}>
          {ajuda}
        </Text>
      )}
      <FormErrorMessage>{extractErrorMessage(error?.message as string)}</FormErrorMessage>
    </FormControl>
  );
}

export function ComexSection({ isDisabled }: { isDisabled?: boolean }) {
  const tomadorExterior = !!useWatch({ name: 'tomador_exterior' });
  const localExterior = !!useWatch({ name: 'local_prestacao_exterior' });
  const exportacao = useWatch({ name: 'trib_issqn' }) === '3';
  const movimentacao = useWatch({ name: 'com_ext.mov_temp_bens' }) as string;
  const { getValues, setValue } = useFormContext();

  // Valor preso em campo que sumiu daria erro sem campo à vista para corrigir (E0354).
  useEffect(() => {
    const limpar = (campo: string) => {
      if (getValues(campo)) setValue(campo, '', { shouldDirty: true });
    };
    if (movimentacao !== '2') limpar('com_ext.n_di');
    if (movimentacao !== '3') limpar('com_ext.n_re');
  }, [movimentacao, getValues, setValue]);

  // Exportação não aceita alíquota, retenção nem dedução (E0602, E0580, E0435).
  useEffect(() => {
    if (!exportacao) return;
    if (getValues('tp_ret_issqn') !== '1') setValue('tp_ret_issqn', '1', { shouldDirty: true });
    ['p_aliq', 'deducao'].forEach(campo => {
      const valor = getValues(campo);
      if (valor !== '' && valor != null) setValue(campo, '', { shouldDirty: true });
    });
  }, [exportacao, getValues, setValue]);

  if (!tomadorExterior && !localExterior && !exportacao) return null;

  return (
    <FormSection n={5} title="Comércio exterior" subtitle="Obrigatório na operação com o exterior">
      <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={4}>
        <CampoLista
          name="com_ext.md_prestacao"
          label="Modo de prestação"
          opcoes={MODO_PRESTACAO_OPCOES}
          placeholder="Selecione"
          isRequired
          isDisabled={isDisabled}
        />
        <CampoLista
          name="com_ext.vinc_prest"
          label="Vínculo entre as partes"
          opcoes={VINCULO_OPCOES}
          isDisabled={isDisabled}
        />
        <CampoLista
          name="com_ext.tp_moeda"
          label="Moeda da operação"
          opcoes={MOEDA_OPCOES}
          placeholder="Selecione"
          isRequired
          isDisabled={isDisabled}
        />
        <CurrencyInputForm
          name="com_ext.v_serv_moeda"
          label="Valor na moeda estrangeira"
          prefix=""
          isRequired
          isDisabled={isDisabled}
        />
        <CampoLista
          name="com_ext.mec_af_comex_p"
          label="Apoio ao comércio exterior · prestador"
          opcoes={MEC_PRESTADOR_OPCOES}
          isDisabled={isDisabled}
        />
        <CampoLista
          name="com_ext.mec_af_comex_t"
          label="Apoio ao comércio exterior · tomador"
          opcoes={MEC_TOMADOR_OPCOES}
          isDisabled={isDisabled}
        />
        <CampoLista
          name="com_ext.mov_temp_bens"
          label="Movimentação temporária de bens"
          opcoes={MOV_TEMP_BENS_OPCOES}
          isDisabled={isDisabled}
        />
        <CampoLista
          name="com_ext.mdic"
          label="Compartilhar com o MDIC"
          opcoes={[
            { value: '0', label: '0 · Não enviar' },
            { value: '1', label: '1 · Enviar' },
          ]}
          isDisabled={isDisabled}
        />
        {movimentacao === '2' && (
          <InputForm
            name="com_ext.n_di"
            label="Nº da Declaração de Importação"
            isRequired
            isDisabled={isDisabled}
          />
        )}
        {movimentacao === '3' && (
          <InputForm
            name="com_ext.n_re"
            label="Nº do Registro de Exportação"
            isRequired
            isDisabled={isDisabled}
          />
        )}
        {exportacao && !localExterior && (
          <PaisBusca
            name="pais_resultado"
            label="País do resultado do serviço"
            isRequired
            isDisabled={isDisabled}
          />
        )}
      </SimpleGrid>
    </FormSection>
  );
}
